import { AnyAction as BaseAction } from 'redux';

import MacroMix, { ValueProps, FunctionProps } from './MacroMix';
import { connect } from 'react-redux';
import serviceContainer from 'src/ServiceContainer';
import * as fp from 'lodash/fp';
import { cascadingFilter } from 'src/utils/Tree/ObjectArrayTree';
import { makelookBackPredicate } from 'src/utils/Pivot/Filter';
import { lookBackPeriodProp } from 'src/utils/Domain/Constants';
import {
  receiveChartData,
  requestChartData,
  selectBox,
  clearChartData,
  requestMacroMixData,
  receiveMacroMixData,
  receiveError as receiveMacroMixError,
  updateLevels,
  requestViewConfig,
  receiveViewConfig,
} from './MacroMix.slice';
import { PointOptions } from 'src/pages/Hindsighting/MacroTrends/GeoTrends/Charts/SimplerChart';
import { makeScopeSensitive } from 'src/components/higherOrder/ScopeSensitive';
import { getTopMembers } from 'src/pages/Hindsighting/MacroTrends/Summary/Summary.container';
import { AppState, AppThunkDispatch, AppObserver } from 'src/store';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { ListDataApi } from 'configuration-schemas/confdefnView';
import { AppType } from 'configuration-schemas/bindings.types';
import { MacroMixOwnProps } from 'configuration-schemas/ownProps';
import type {
  MacroMixLineGraph,
  MacroMixDropdowns,
  MacroMixHeatMapToolTip,
  MacroMixHeatMap,
  MacroMixViewDefn,
} from 'configuration-schemas/viewdefns/viewdefn';
import { zMacroMixViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { toastAndLog } from 'src/services/loggingService';

const observers: (() => void)[] = [];
type ViewdefnWithXType = Pick<MacroMixViewDefn['view'][0], 'xtype'>;
const isHeatmapConf = (v: ViewdefnWithXType): v is MacroMixHeatMap => v.xtype === 'heatmap-conf';
const isHeatmapSelectionsConf = (v: ViewdefnWithXType): v is MacroMixDropdowns => v.xtype === 'heatmap-selections';
const isChartConf = (v: ViewdefnWithXType): v is MacroMixLineGraph =>
  v.xtype === 'left-chart' || v.xtype === 'right-chart';
const isTooltipConf = (v: ViewdefnWithXType | undefined): v is MacroMixHeatMapToolTip =>
  !!v && v.xtype === 'description';

const findViewByXType = (typeToFind: MacroMixViewDefn['view'][0]['xtype'], conf: MacroMixViewDefn['view']) => {
  return conf.find((view) => {
    return view.xtype === typeToFind;
  });
};

function mapStateToProps(state: AppState, ownProps: MacroMixOwnProps): ValueProps {
  const { macroMix } = state.pages.hindsighting;
  const { lookBackPeriod } = state.subheader;
  const { title = 'Macro Mix', showFlowStatus, showLookBackPeriod } = ownProps;
  const initialRender: ValueProps = {
    // first render
    loaded: false,
    title,
    showFlowStatus,
    showLookBackPeriod,
  };

  if (!macroMix.confLoaded || !macroMix.allConf) {
    return initialRender;
  }

  // get confs
  const heatDropdownConf = findViewByXType('heatmap-selections', macroMix.allConf.view);
  const heatmapConf = findViewByXType('heatmap-conf', macroMix.allConf.view);
  const leftChartConf = findViewByXType('left-chart', macroMix.allConf.view);
  const rightChartConf = findViewByXType('right-chart', macroMix.allConf.view);
  const toolTipConf = findViewByXType('description', macroMix.allConf.view);

  // the type checker doesn't like it if all of these aren't in the if statement
  if (
    !fp.isUndefined(heatDropdownConf) &&
    !fp.isUndefined(heatmapConf) &&
    !fp.isUndefined(leftChartConf) &&
    !fp.isUndefined(rightChartConf) &&
    isHeatmapSelectionsConf(heatDropdownConf) &&
    isHeatmapConf(heatmapConf) &&
    isChartConf(leftChartConf) &&
    isChartConf(rightChartConf) &&
    macroMix.data &&
    macroMix.regions &&
    macroMix.selectedLevels
    // macroMix.chartData
  ) {
    let data = macroMix.data;
    if (lookBackPeriod && showLookBackPeriod) {
      // Filter by lookBackPeriod
      data = cascadingFilter(data, makelookBackPredicate(lookBackPeriod));
    }
    return {
      loaded: true,
      chartDataLoaded: macroMix.chartDataLoaded,
      dataLoaded: macroMix.dataLoaded,
      selectedBox: macroMix.selectedBox,
      selectedLevels: macroMix.selectedLevels,
      data: data,
      regions: macroMix.regions,
      chartData: macroMix.chartData,
      dropdownConf: heatDropdownConf,
      heatmapConf: heatmapConf,
      leftChartConf: leftChartConf,
      rightChartConf: rightChartConf,
      title,
      toolTipConf: isTooltipConf(toolTipConf) ? toolTipConf : undefined,
      showFlowStatus,
      showLookBackPeriod,
    };
  }
  // this shouldn't be hit
  return initialRender;
}

function asyncGetData(dataApi: ListDataApi) {
  const service = serviceContainer.pivotService;
  return (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const state = getState();
    const { macroMix } = state.pages.hindsighting;
    const { flowStatus, lookBackPeriod } = getState().subheader;
    const selectedLevels = macroMix.selectedLevels;

    if (!fp.isNil(selectedLevels) && selectedLevels.view) {
      // FIXME: Can we use the service cache here? This seems like the perfect view to have it
      const listDataPromise = service.listData(dataApi.defnId, dataApi.params?.appName, {
        aggBy: selectedLevels.view.map((it) => `${it.dimension}:${it.dataIndex}`).join(','),
        flowStatus: flowStatus.join(','),
        [lookBackPeriodProp]: lookBackPeriod,
        topMembers: getTopMembers(getState().scope.scope),
        nestData: false,
      });
      const regionLevel = selectedLevels.view[1].dataIndex;
      const regionMembersPromise = service.getValidMembers(regionLevel);

      dispatch(requestMacroMixData());
      return Promise.all([listDataPromise, regionMembersPromise])
        .then(([listData, regions]) => {
          return dispatch(
            receiveMacroMixData({
              listData: listData.tree,
              regions: regions,
            }),
          );
        })
        .catch((error) =>
          dispatch(
            receiveMacroMixError({
              type: ComponentErrorType.data,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.macroMix,
              defnId: dataApi.defnId,
            }),
          ),
        );
    }
    return Promise.resolve();
  };
}

function asyncGetChartData(chartDataApi: ListDataApi) {
  const service = serviceContainer.pivotService;

  return (dispatch: AppThunkDispatch, getState: () => AppState): Promise<void> | void => {
    const { selectedBox } = getState().pages.hindsighting.macroMix;

    if (selectedBox) {
      dispatch(requestChartData());
      service
        .listData(chartDataApi.defnId, chartDataApi.params?.appName, {
          aggBy: chartDataApi.params?.aggBy,
          topMembers: [selectedBox.id, selectedBox.mId].join(','),
        })
        .then((resp) => {
          dispatch(receiveChartData(resp.tree));
        })
        .catch((err) => {
          toastAndLog('An error occurred fetching chart data', err);
          dispatch(receiveChartData([]));
        });
    }
  };
}

function mapDispatchToProps(dispatch: AppThunkDispatch, ownProps: MacroMixOwnProps): FunctionProps {
  const { tenantConfigClient } = serviceContainer;
  const { defns } = ownProps;
  return {
    onShowView() {
      dispatch(requestViewConfig());
      tenantConfigClient
        .getTenantViewDefns<MacroMixViewDefn>({
          defnIds: defns.view,
          appName: AppType.Assortment,
          validationSchemas: [zMacroMixViewDefn],
        })
        .then((resp) => {
          const conf = resp[0];
          dispatch(receiveViewConfig(conf));

          // pull off the first viewdata item to load the default metric
          return findViewByXType('heatmap-selections', conf.view);
        })
        .then((dropdownConf) => {
          // TODO: add default selection to viewDefn?
          if (dropdownConf && isHeatmapSelectionsConf(dropdownConf)) {
            const defaultItem = dropdownConf.view[0];
            dispatch(updateLevels(defaultItem));
          }
          dispatch(asyncGetData(ownProps.dataApi));
          observers.push(
            AppObserver.on('subheader.flowStatus', () => {
              dispatch(asyncGetData(ownProps.dataApi));
            }),
          );
        })
        .catch((error) =>
          dispatch(
            receiveMacroMixError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.macroMix,
              issues: error,
              defnId: error.defnId,
            }),
          ),
        );
    },
    onUpdateLevel(newLevels: MacroMixDropdowns['view'][0]) {
      dispatch(clearChartData());
      dispatch(updateLevels(newLevels));
      dispatch(requestMacroMixData());
      dispatch(asyncGetData(ownProps.dataApi));
    },
    onSelectBox(selectedBox: PointOptions) {
      dispatch(selectBox(selectedBox));
      dispatch(requestChartData());
      dispatch(asyncGetChartData(ownProps.chartDataApi));
    },
    onDestroy() {
      observers.forEach((e) => e());
      observers.length = 0;
    },
    onRefetchData() {
      dispatch(clearChartData());
      dispatch(asyncGetData(ownProps.dataApi));
    },
  };
}

const scopeSensitiveComponent = makeScopeSensitive(MacroMix);
// ignore the contravariant parameter issue caused by loaded true/false. Will figure out better solution in future
// @ts-ignore: TS2345
export default connect(mapStateToProps, mapDispatchToProps)(scopeSensitiveComponent);
